import { SELECTION_TYPES } from "@/schemas/genericSelectionSchema";
import { ferryPassengerGeneralValidations } from "./generalValidations";
import { createFerryPassengerCustomValidations } from "./customValidations";

/**
 * Otobüs yolcu seçimi konfigürasyonu
 * i18n desteği ile
 */

// Passenger types for ferry
export const FERRY_PASSENGER_TYPES = {
  ADULT: "adult",
  YOUNG: "young",
  CHILD: "child",
};

/**
 * Otobüs yolcu seçim konfigürasyonu
 * @param {Object} options - Customization options
 * @param {Number} options.maxPassengers - Maksimum yolcu sayısı
 * @param {Number} options.minPassengers - Minimum yolcu sayısı
 * @param {Number} options.currentAdultCount - Şu anki adult sayısı (dynamic max hesaplaması için)
 * @param {Number} options.currentChildCount - Şu anki child sayısı (dynamic max hesaplaması için)
 * @param {Object} options.badges - Badge'ler { slug: "badge metni" }
 *   Örnek: { young: "%10 indirim", adult: "Popüler", child: "Ücretsiz" }
 * @returns {Object} Configuration object
 */
export const createFerryPassengerConfig = ({
  maxPassengers = 9,
  minPassengers = 1,
  currentAdultCount = 0,
  currentChildCount = 0,
  isWithVehicle = false,
  badges = {},
} = {}) => {
  const children = [
    {
      type: SELECTION_TYPES.INCREMENT,
      slug: FERRY_PASSENGER_TYPES.ADULT,
      labelKey: "ferry.passengerAdult",
      label: "Yetişkin",
      descriptionKey: isWithVehicle
        ? "ferry.passengerAdultDescWithVehicle"
        : "ferry.passengerAdultDesc",
      description: "24 ve üstü",
      min: 1,
      max: maxPassengers - currentChildCount, // Dinamik max: toplam - child
      value: 1,
      badge: badges?.adult, // Dışardan gelen badge
    },
    {
      type: SELECTION_TYPES.INCREMENT,
      slug: FERRY_PASSENGER_TYPES.CHILD,
      labelKey: "ferry.passengerChild",
      label: "Çocuk",
      descriptionKey: "ferry.passengerChildDesc",
      description: "2-11 yaş",
      min: 0,
      max: maxPassengers - currentAdultCount, // Dinamik max: toplam - adult
      value: 0,
      badge: badges?.child, // Dışardan gelen badge
    },
  ];

  return {
    settings: {
      totalSelections: maxPassengers,
      minTotalSelections: minPassengers,
      allowZeroSelections: false,
      // Ferry özel genel validasyonları (ayrı dosyadan)
      generalValidations: ferryPassengerGeneralValidations,

      // field bazında validasyonlar
      customValidations: createFerryPassengerCustomValidations(
        FERRY_PASSENGER_TYPES,
      ),
    },
    children: [
      {
        type: SELECTION_TYPES.GROUP,
        //labelKey: "ferry.passengerTitle",
        //label: "Yolcu Sayısı", // Fallback
        children,
        className: "divide-y divide-primary-weak",
      },
    ],
  };
};

/**
 * Display formatter for ferry passengers
 * @param {Object} values - Current values
 * @param {Function} t - Translation function
 * @returns {String} Display text
 */
export const ferryPassengerDisplayFormatter = (values, t) => {
  const adultCount = values[FERRY_PASSENGER_TYPES.ADULT] || 0;
  const youngCount = values[FERRY_PASSENGER_TYPES.YOUNG] || 0;
  const childCount = values[FERRY_PASSENGER_TYPES.CHILD] || 0;

  const total = adultCount + youngCount + childCount;

  if (total === 0) {
    return t ? t("ferry.passengerSelect") : "Yolcu Seçin";
  }

  const parts = [];

  // Yetişkin ve genç birlikte göster
  const regularPassengers = adultCount + youngCount + childCount;
  if (regularPassengers > 0) {
    const passengerText = t ? t("ferry.passenger") : "Yolcu";
    parts.push(`${regularPassengers} ${passengerText}`);
  }

  // Append selected vehicle if any

  if (values?.isWithVehicle) {
    const vt = values?.vehicleType;
    const vehicleLabel = vt?.label;
    if (vehicleLabel) {
      parts.push(vehicleLabel);
    }
  }

  return parts.join(", ");
};

/**
 * Dynamic config creator
 * @param {Object} options - Dynamic options
 * @param {Object} options.tempPassengers - Temp passengers from useFerrySearch hook
 * @param {Number} options.maxPassengers - Maximum passengers (optional, defaults to 9)
 * @param {Number} options.minPassengers - Minimum passengers (optional, defaults to 1)
 * @param {Object} options.badges - Custom badges (optional)
 * @returns {Object} Configuration object
 */
export const createDynamicFerryPassengerConfig = ({
  tempPassengers,
  maxPassengers = 9,
  minPassengers = 1,
  isWithVehicle = false,
  badges = {},
} = {}) => {
  return createFerryPassengerConfig({
    maxPassengers,
    minPassengers,
    currentAdultCount: tempPassengers?.adult || 0,
    currentChildCount: tempPassengers?.child || 0,
    isWithVehicle,
    badges,
  });
};

/**
 * Varsayılan config
 */
export const defaultFerryPassengerConfig = createFerryPassengerConfig();
